import React, { useEffect } from 'react';

import { IconMailHeart, IconSendingMail } from '@trqk/components';
import { constants, locale } from '@trqk/utils';
import { useGetTrqkUserInvitationsLazyQuery } from '@trqk/api';

import { InvitationForm, RemainingInvites } from './components';

/**
 * @typedef {ProfileProps}
 * @property {User} currentUser the currently logged in user
 */
interface InviteOthersProps {
    currentUser: User;
}

const { MAX_INVITES } = constants;

/**
 * A section of the Account page that allows the user to invite others to join Trqk.
 * @param props {InviteOthersProps}
 */
export const InviteOthers = ({
    currentUser,
}: InviteOthersProps): JSX.Element => {
    const [getTrqkUser, getTrqkUserQuery] =
        useGetTrqkUserInvitationsLazyQuery();

    useEffect(() => {
        if (currentUser.id) {
            getTrqkUser({
                variables: {
                    id: currentUser.id,
                },
            });
        }
    }, [currentUser.id]);

    const invitees = getTrqkUserQuery?.data?.getTrqkUser?.invitations ?? [];

    return (
        <div
            className="px-12 py-10 bg-white rounded-lg"
            style={{ height: 560 }}
        >
            <div className="flex items-center">
                <IconMailHeart fillColor="primary-500" width={35} />
                <h2
                    className="text-xl ml-2"
                    data-cy="invite-others-section-title"
                >
                    {locale.enUS.ACCOUNT.SECTIONS.INVITE_OTHERS.TITLE}
                </h2>
            </div>
            <div className="flex py-10 h-full">
                <div className="w-1/3 pr-10">
                    <RemainingInvites
                        invitesRemaining={Math.max(
                            0,
                            MAX_INVITES - invitees.length,
                        )}
                    />
                </div>
                <div className="w-1/3 h-full">
                    <InvitationForm
                        currentUser={currentUser}
                        disableSubmit={invitees.length >= MAX_INVITES}
                    />
                </div>
                <div className="w-1/3 pl-10 h-full">
                    <div className="pb-2 mb-2 border-b border-tertiary-200 text-tertiary-400 italic">
                        {
                            locale.enUS.ACCOUNT.SECTIONS.INVITE_OTHERS
                                .INVITES_SENT
                        }
                    </div>
                    <div className="h-full pb-10">
                        <div className="h-full overflow-y-auto">
                            {invitees.map(
                                ({
                                    first_name,
                                    last_name,
                                    email,
                                    company,
                                }: TrqkInvitation): JSX.Element | null => {
                                    if (!email) {
                                        return null;
                                    }

                                    return (
                                        <div
                                            key={email}
                                            className="py-2 px-2 text-sm"
                                        >
                                            <div className="font-semibold truncate">
                                                {`${first_name} ${last_name}`}
                                                {company && (
                                                    <>{` | ${company}`}</>
                                                )}
                                            </div>
                                            <div className="text-tertiary-400 flex">
                                                <IconSendingMail
                                                    addClasses="flex-none"
                                                    fillColor="tertiary-300"
                                                />
                                                <div className="ml-2 truncate">
                                                    {email}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
